import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react'
import EditorialComponentsFactory from './editorial-components-factory'
import {getFirstHeroBannerVideo} from './utils'
import {Helmet} from 'react-helmet'
import {useEditorialVideoBaseUrl} from '../../custom-hooks/use-dam-image'

/**
 * Component types that don't render visible content above the fold.
 * These are skipped when determining the first visual element (used for LCP optimizations
 * like fetchpriority and preload on hero banners with images or videos).
 */
const LAYOUT_ONLY_TYPES = ['padding_block']

/**
 * Determines whether a component from Contentstack is a real visual component.
 *
 * Two cases are filtered out:
 * 1. Layout-only types (e.g. padding_block) — they don't render visible content
 * 2. Empty references — Contentstack sometimes returns entries with only `uid`
 *    and `_content_type_uid`, without any actual content fields (e.g. unpublished
 *    or misconfigured references). We detect them by checking that the component
 *    has at least one non-internal field beyond `uid`.
 */
const isVisualComponent = (component) => {
    if (!component) return false
    if (LAYOUT_ONLY_TYPES.includes(component._content_type_uid)) return false

    // Internal Contentstack fields start with "_" (e.g. _content_type_uid, _version).
    // A component with only uid + internal fields has no actual content to render.
    const keys = Object.keys(component).filter((k) => !k.startsWith('_') && k !== 'uid')
    return keys.length > 0
}

const RenderComponents = ({
    pageComponents,
    //blogsPage,
    contentTypeUid,
    entryUid,
    locale,
}) => {
    /**
     * Index of the first component that renders visible content.
     * Passed down as `isFirstElement` to allow components like HeroBanner
     * to apply LCP optimizations (fetchpriority, preload="auto", poster, etc.).
     * Returns -1 if no visual component is found (no component will get isFirstElement=true).
     */
    const firstVisualIndex = Array.isArray(pageComponents)
        ? pageComponents.findIndex(isVisualComponent)
        : -1

    const firstVisualComponent = firstVisualIndex >= 0 ? pageComponents[firstVisualIndex] : null
    const videoBaseUrl = useEditorialVideoBaseUrl()

    /**
     * Preload the video only if the first visual component is a hero banner with videos.
     * This avoids preloading resources for pages where the LCP element is an image instead.
     */
    const firstComponentIsVideoHero =
        firstVisualComponent?._content_type_uid === 'herobanner' &&
        !!firstVisualComponent?.videos?.desktop &&
        !!firstVisualComponent?.videos?.mobile

    const firstHeroBannerVideo = firstComponentIsVideoHero
        ? getFirstHeroBannerVideo(pageComponents, videoBaseUrl)
        : null

    return (
        <>
            {/*
             * firstHeroBannerVideo is null when the first visual element is not a video hero banner,
             * so the <link> tags below will not be rendered.
             */}
            <Helmet>
                {firstHeroBannerVideo?.mobile && (
                    <link
                        rel="preload"
                        as="video"
                        href={firstHeroBannerVideo.mobile}
                        fetchPriority="high"
                        type="video/mp4"
                    />
                )}
                {firstHeroBannerVideo?.desktop && (
                    <link
                        rel="preload"
                        as="video"
                        href={firstHeroBannerVideo.desktop}
                        fetchPriority="high"
                        type="video/mp4"
                    />
                )}
            </Helmet>
            <Box data-pageref={entryUid} data-contenttype={contentTypeUid} data-locale={locale}>
                {Array.isArray(pageComponents) &&
                    pageComponents.length > 0 &&
                    pageComponents.map((component, index) => {
                        const componentType = component?._content_type_uid
                        return (
                            <Fragment key={index}>
                                <EditorialComponentsFactory
                                    componentType={componentType}
                                    isFirstElement={index === firstVisualIndex}
                                    {...component}
                                />
                            </Fragment>
                        )
                    })}
            </Box>
        </>
    )
}

RenderComponents.propTypes = {
    pageComponents: PropTypes.arrayOf(
        PropTypes.shape({
            hero_banner: PropTypes.object,
            section: PropTypes.object,
            section_with_buckets: PropTypes.shape({
                bucket_tabular: PropTypes.bool,
            }),
            from_blog: PropTypes.array,
            section_with_cards: PropTypes.shape({
                cards: PropTypes.array,
            }),
            section_with_html_code: PropTypes.object,
            our_team: PropTypes.object,
        }),
    ),
    blogsPage: PropTypes.bool,
    contentTypeUid: PropTypes.string.isRequired,
    entryUid: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
}

export default React.memo(RenderComponents)
